import { defineStore } from "pinia";

import type { State } from "./Models/State";
import type { PatientInfoUpdateQuery } from "@/Modules/Patients/Models/Domain";
import type { PlanToPatientApiQuery } from "@/Modules/Patients/Models/Api";
import type { LegalAgreement } from "@/Modules/Models/Domain/LegalAgreement";
import { NotificationType } from "@/Modules/App/Models/Domain/NotificationType";
import { StateNamespace } from "@/Modules/App/Models/State/StateNamespace";

import fetchPatientsAH from "./ActionHandlers/FetchPatients";
import fetchPlansAH from "./ActionHandlers/FetchPlans";
import fetchStatesAH from "./ActionHandlers/FetchStates";
import fetchPatientAH from "./ActionHandlers/FetchPatient";
import filterPatientsBySearchAH from "./ActionHandlers/FilterPatientsBySearch";
import filterPatientsByPlanAH from "./ActionHandlers/FilterPatientsByPlan";
import filterPatientsByStatusAH from "./ActionHandlers/FilterPatientsByStatus";
import requestTranscriptionAH from "./ActionHandlers/RequestTranscription";
import sendMissingEmailAH from "../Patients/ActionHandlers/SendMissingEmail";
import dischargePatientAH from "../Patients/ActionHandlers/DischargePatient";
import updatePatientAH from "../Patients/ActionHandlers/UpdatePatient";
import sendPlanToPatientAH from "../Patients/ActionHandlers/SendPlanToPatient";
import sendLegalAgreementsAH from "./ActionHandlers/SendLegalAgreement";

import useAppStore from "@/Modules/App/Store";

import { hasValue } from "@/Utils/Objects";
import { copyToClipboard } from "@/Utils/CopyToClipboard";

const patientsState: State = {
  patients: {},
  selected: null,
  isLoading: false,
  notification: null,
  privatePlans: null,
  sessionsPaymentsRecords: null,
  plans: null,
  states: null,
  isFiltering: false,
};

export const usePatientsStore = defineStore(StateNamespace.PATIENTS, {
  state: () => ({ ...patientsState }),

  actions: {
    fetchPatients() {
      return fetchPatientsAH();
    },

    fetchPatient(patientUuid: string) {
      return fetchPatientAH(patientUuid);
    },

    filterPatientsByPlan(planUuid: string) {
      return filterPatientsByPlanAH(planUuid);
    },

    filterPatientsBySearch(search: string) {
      return filterPatientsBySearchAH(search);
    },

    filterPatientsByStatus(status: string) {
      return filterPatientsByStatusAH(status);
    },

    fetchPlans() {
      return fetchPlansAH();
    },

    fetchStates() {
      return fetchStatesAH();
    },

    selectPatient(patientUuid: string) {
      if (hasValue(this.patients[patientUuid])) {
        this.selected = this.patients[patientUuid];
      }
    },

    requestTranscription(status: boolean): Promise<boolean | undefined> {
      return requestTranscriptionAH(status);
    },

    sendMissingEmail(patientUuid: string): Promise<boolean | undefined> {
      return sendMissingEmailAH(patientUuid);
    },

    dischargePatient(patientUuid: string, reason: string): Promise<boolean | undefined> {
      return dischargePatientAH(patientUuid, reason);
    },

    updatePatient(patientUuid: string, personalData: PatientInfoUpdateQuery) {
      return updatePatientAH(patientUuid, personalData);
    },

    sendPlanToPatient(patientUuid: string, infoPlan: PlanToPatientApiQuery) {
      return sendPlanToPatientAH(patientUuid, infoPlan);
    },

    async sendLegalAgreement(patientUuid: string, contractUUID: string): Promise<LegalAgreement | undefined> {
      return sendLegalAgreementsAH(patientUuid, contractUUID);
    },

    clearNotification() {
      this.notification = null;
    },

    clear() {
      this.isLoading = false;
      this.notification = null;
    },

    copyPatientId(uuid: string) {
      const appStore = useAppStore();

      copyToClipboard(uuid);

      appStore.showSnackbar({
        message: "patients.field.id.notification_copy_message",
        type: NotificationType.INFO,
      });
    },
  },

  getters: {},
});

export type PatientsStore = ReturnType<typeof usePatientsStore>;

export default usePatientsStore;
